import { Plus, X } from '@/components/ui/icons';
import { useState, type FormEvent } from 'react';
import { LIMITS, POLL_DURATIONS_HOURS, type MessageTargetRef } from '@clanfire/shared';
import { Button, IconButton } from '@/components/ui/Button';
import { Input, Select, Switch } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { createPoll } from '@/lib/messaging';

const durationLabel = (hours: number) => (hours < 24 ? `${hours} hour${hours === 1 ? '' : 's'}` : hours === 168 ? '1 week' : `${hours / 24} day${hours === 24 ? '' : 's'}`);

export function PollModal({ target, onClose }: { target: MessageTargetRef; onClose: () => void }) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [durationHours, setDurationHours] = useState(24);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const filled = options.map((o) => o.trim()).filter(Boolean);
  const setOption = (i: number, value: string) => setOptions((list) => list.map((o, j) => (j === i ? value : o)));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!question.trim()) return setError('Ask a question.');
    if (filled.length < LIMITS.pollOptionsMin) return setError(`Add at least ${LIMITS.pollOptionsMin} answers.`);
    setError(null);
    setSaving(true);
    const ok = await createPoll(target, { question: question.trim(), options: filled, allowMultiple, durationHours });
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Create a poll"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="create-poll" loading={saving}>
            Post poll
          </Button>
        </>
      }
    >
      <form id="create-poll" onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
        <Input label="Question" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="What game tonight?" maxLength={LIMITS.pollQuestionMax} data-autofocus />
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1.5 text-xs font-bold tracking-wide text-fg-soft uppercase">Answers</legend>
          {options.map((option, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1">
                <Input aria-label={`Answer ${i + 1}`} value={option} onChange={(e) => setOption(i, e.target.value)} placeholder={`Answer ${i + 1}`} maxLength={LIMITS.pollOptionMax} />
              </div>
              {options.length > LIMITS.pollOptionsMin && (
                <IconButton label={`Remove answer ${i + 1}`} size="sm" onClick={() => setOptions((list) => list.filter((_, j) => j !== i))}>
                  <X size={16} />
                </IconButton>
              )}
            </div>
          ))}
          {options.length < LIMITS.pollOptionsMax && (
            <Button variant="ghost" size="sm" icon={<Plus size={15} />} onClick={() => setOptions((list) => [...list, ''])} className="self-start">
              Add another answer
            </Button>
          )}
        </fieldset>
        <Select label="Voting closes after" value={String(durationHours)} onChange={(e) => setDurationHours(Number(e.target.value))}>
          {POLL_DURATIONS_HOURS.map((h) => (
            <option key={h} value={h}>
              {durationLabel(h)}
            </option>
          ))}
        </Select>
        <Switch checked={allowMultiple} onChange={setAllowMultiple} label="Allow more than one answer" />
        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}
